import { useQueries } from "@tanstack/react-query";
import {
    SubscriptionSection,
    TimeSubscription,
    useAccessToken,
    useStandardErrorHandler,
} from "modules/common";
import { PaginatedResponse } from "modules/dashboard";
import { createService } from "services/serviceFactory";
import {
    AdAccountsRequest,
    UpdateModuleManagementRequest,
    UpdateMyAccountMetadataRequest,
    UpdateNotificationSubscriptionRequest,
    UpdateProfileRequest,
    UserAccountsRequest,
} from "./request";
import {
    AccountDashboardDataResponse,
    AdAccounts,
    FilterPreferenceResponse,
    ModuleManagementDataResponse,
    NotificationSubscriptionsForCategoryResponse,
    ProfileResonse,
    UpdateFilterPreferencesRequest,
    UploadAttachmentResponse,
    UserAccountResponse,
    UserAccountsByUidResponse,
    UserAccountsCountResponse,
    UserAccountsResponse,
} from "./response";

const accountService = createService("account");

async function getUserAccounts(data: UserAccountsRequest, accessToken: string) {
    return accountService.post<UserAccountsResponse>("/account/v1/Account/GetUserAccounts", data, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getAdUserAccounts(data: AdAccountsRequest, accessToken: string) {
    return accountService.post<PaginatedResponse<AdAccounts>>(
        "/account/v1/Account/GetAdUserAccounts",
        data,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function getUserAccountsCount(data: Partial<UserAccountsRequest>, accessToken: string) {
    return accountService.post<UserAccountsCountResponse>(
        "/account/v1/Account/GetUserAccountsCount",
        data,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function getUserAccountsByUids(uids: string[], accessToken: string) {
    return accountService.post<UserAccountsByUidResponse>(
        "/account/v1/Account/GetUserAccountsByUids",
        uids,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function getUserAccount(uid: string, userRole: number, accessToken: string) {
    return accountService.post<UserAccountResponse>(
        "/account/v1/Account/GetUserAccount",
        {
            uid,
            userRole,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function activateUser(
    uid: string,
    userRole: number,
    isDeactivate: boolean,
    accessToken: string
) {
    return accountService.post(
        "/account/v1/Account/ActivateUserAccount",
        {
            uid,
            userRole,
            isDeactivate,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function updateRoles(uid: string, roleType: number, accessToken: string) {
    return accountService.post(
        "/account/v1/Account/UpdateUserAccountRoles",
        {
            uid,
            roleType,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function getMyAccount(accessToken: string) {
    return accountService.get<ProfileResonse>("/account/v1/Account/GetMyAccount", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function updateMyAccountSettings(requestData: UpdateProfileRequest, accessToken: string) {
    return accountService.post(
        "/account/v1/Account/UpdateMyAccountSettings",
        {
            ...requestData,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function updateMyAccountMobileNumber(
    mobileNumber: string,
    additionalMobileNumber: string,
    accessToken: string
) {
    return accountService.post(
        "/account/v1/Account/UpdateMyAccountMobileNumber",
        {
            mobileNumber,
            additionalMobileNumber,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function updateMyAccountMetadata(
    requestData: UpdateMyAccountMetadataRequest,
    accessToken: string
) {
    return accountService.post(
        "/account/v1/Account/UpdateMyAccountMetadata",
        {
            ...requestData,
        },
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function deleteMyAccount(accessToken: string) {
    return accountService.delete("/account/v1/Account/DeleteMyAccount", {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function getUserAccountDashboardData(accessToken: string) {
    return accountService.get<AccountDashboardDataResponse>(
        "/account/v1/Account/GetUserAccountDashboardData",
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
}

async function getAllCounties() {
    return accountService.get("/account/v1/Account/GetAllCounties");
}

async function getSettlementsForCounty(countyValue: number) {
    return accountService.get("/account/v1/Account/GetSettlementsByCountyValue", {
        params: {
            countyValue,
        },
    });
}

async function getSettlementsForCounties(countyValues: number[]) {
    return accountService.get("/account/v1/Account/GetSettlementsByCountyValues", {
        params: { countyValues },
    });
}

async function getUserFarmerIds(oib: string, accessToken: string) {
    return accountService.get<{ farmerIds: number[] }>("/account/v1/Account/GetUserFarmerId", {
        params: {
            oib,
        },
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

async function uploadAttachment(accessToken: string, body: FormData) {
    return accountService.post<UploadAttachmentResponse>(
        "/account/v1/Notification/uploadAttachment",
        body,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
                "Content-Type": "multipart/form-data",
            },
        }
    );
}

async function getFilterPreferences(accessToken: string, filterName: string) {
    const response = await accountService.get<FilterPreferenceResponse>(
        "/account/v1/Account/getFilterPreferences",
        {
            params: {
                filterName,
            },
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data.filterPreferences;
}

async function updateFilterPreferences(
    updateFilterPreferencesRequest: UpdateFilterPreferencesRequest,
    accessToken: string
) {
    const response = await accountService.post(
        "/account/v1/Account/updateFilterPreferences",
        updateFilterPreferencesRequest,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data;
}

async function updateModuleManagement(
    updateModuleManagementRequest: UpdateModuleManagementRequest,
    accessToken: string
) {
    const response = await accountService.post(
        "/account/v1/Account/UpdateModuleManagement",
        updateModuleManagementRequest,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data;
}

async function updateNotificationSubscription(
    updateNotificationSubscriptionRequest: UpdateNotificationSubscriptionRequest,
    accessToken: string
) {
    const response = await accountService.post(
        "/account/v1/Account/updateNotificationSubscription",
        updateNotificationSubscriptionRequest,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data;
}

export const useGetNotificationSubscriptionsForCategory = (
    isOpen: boolean,
    subscriptionCategories: SubscriptionSection[]
) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju pretplati");
    const accessToken = useAccessToken();

    const queries = useQueries({
        queries: subscriptionCategories.map(group => ({
            queryKey: ["subscriptions", group.subscriptions[0]?.category],
            queryFn: () =>
                getNotificationSubscriptionsForCategory(
                    accessToken ?? "",
                    group.subscriptions[0]?.category
                ),
            onError: errorHandler,
            keepPreviousData: true,
            enabled: Boolean(accessToken && isOpen),
        })),
    });

    return queries;
};

async function getNotificationSubscriptionsForCategory(accessToken: string, category: string) {
    const response = await accountService.get<NotificationSubscriptionsForCategoryResponse>(
        "/account/v1/Account/GetNotificationSubscriptionsForCategory",
        {
            params: {
                category,
            },
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data.subscriptions;
}

async function getModuleManagementData(accessToken: string) {
    const response = await accountService.get<ModuleManagementDataResponse>(
        "/account/v1/Account/GetModuleManagementData",
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data.modules;
}

async function getEducationTimeSubscription(accessToken: string) {
    const response = await accountService.get<TimeSubscription>(
        "/account/v1/Account/GetEducationTimeSubscription",
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data;
}

async function updateEducationTimeSubscription(accessToken: string, body: TimeSubscription) {
    const response = await accountService.post<TimeSubscription>(
        "/account/v1/Account/UpdateEducationTimeSubscription",
        body,
        {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    return response.data;
}

async function getNotificationRecipientsStatistics(body: any, accessToken: string) {
    return accountService.post<any>("/account/v1/Account/GetMessageRecipientsStatistics", body, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
        },
    });
}

export {
    activateUser,
    deleteMyAccount,
    getAdUserAccounts,
    getAllCounties,
    getEducationTimeSubscription,
    getFilterPreferences,
    getModuleManagementData,
    getMyAccount,
    getNotificationRecipientsStatistics,
    getNotificationSubscriptionsForCategory,
    getSettlementsForCounties,
    getSettlementsForCounty,
    getUserAccount,
    getUserAccountDashboardData,
    getUserAccounts,
    getUserAccountsByUids,
    getUserAccountsCount,
    getUserFarmerIds,
    updateEducationTimeSubscription,
    updateFilterPreferences,
    updateModuleManagement,
    updateMyAccountMetadata,
    updateMyAccountMobileNumber,
    updateMyAccountSettings,
    updateNotificationSubscription,
    updateRoles,
    uploadAttachment,
};
